import { zodResolver } from '@hookform/resolvers/zod';
import SelectControlledWithLabel from 'components/shared/web-components/SelectControlledWithLabel';
import TextFieldControlledWithLabel from 'components/shared/web-components/TextFieldControlledWithLabel';
import { useGetCountryName } from 'hooks/ppoints/useGetCountryName';
import { useCountries } from 'hooks/useCountries';
import ModalActions from 'layouts/modal-actions/modal-actions';
import { requestPackageProviderFeatureMutation } from 'mutations/package-provider-feature-request/requestPackageProviderFeature';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

const requestProviderSchema = z.object({
    provider: z.string().min(1, { message: 'SHIPPING_PROVIDERS.REQUEST_PROVIDER.MODAL.PROVIDER_NAME.REQUIRED' }),
    countryCode: z.string().min(1, { message: 'SHIPPING_PROVIDERS.REQUEST_PROVIDER.MODAL.COUNTRY.REQUIRED' }),
    hasAccess: z.enum(['yes', 'no']),
    description: z.string().optional(),
});

type RequestProviderFormValues = z.infer<typeof requestProviderSchema>;

export const RequestProviderModal = ({ onSuccess, selectedCountryCode, providerName }: RequestProviderModalProps) => {
    const { t } = useTranslation();
    const { data: countriesQuery, isLoading: isLoadingCountries } = useCountries();
    const { getTranslatedCountryName } = useGetCountryName();

    const countryOptions = useMemo(
        () =>
            countriesQuery?.map((country) => ({
                label: getTranslatedCountryName(country.country_code),
                value: country.country_code,
            })) ?? [],
        [countriesQuery, getTranslatedCountryName]
    );

    const hasAccessOptions = useMemo(
        () => [
            { label: t('SHIPPING_PROVIDERS.REQUEST_PROVIDER.MODAL.HAS_ACCESS.YES'), value: 'yes' },
            { label: t('SHIPPING_PROVIDERS.REQUEST_PROVIDER.MODAL.HAS_ACCESS.NO'), value: 'no' },
        ],
        [t]
    );

    const form = useForm<RequestProviderFormValues>({
        mode: 'onBlur',
        resolver: zodResolver(requestProviderSchema),
        defaultValues: {
            provider: providerName ?? '',
            countryCode: selectedCountryCode ?? '',
            hasAccess: 'no',
            description: '',
        },
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasError, setHasError] = useState(false);

    const handleRequestProvider = (data: RequestProviderFormValues) => {
        setIsSubmitting(true);
        setHasError(false);
        requestPackageProviderFeatureMutation({
            provider: data.provider,
            countryCode: data.countryCode,
            hasAccess: data.hasAccess === 'yes',
            description: data.description,
        })
            .then(() => {
                shopify.toast.show(t('SHIPPING_PROVIDERS.REQUEST_PROVIDER.MODAL.TOAST.SUCCESS'));
                onSuccess();
            })
            .catch(() => {
                setHasError(true);
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <FormProvider {...form}>
            <div className="p-4">
                {hasError && (
                    <div className="pb-4">
                        <s-banner tone="critical" heading={t('SHIPPING_PROVIDERS.REQUEST_PROVIDER.MODAL.ERROR.TITLE')}>
                            {t('SHIPPING_PROVIDERS.REQUEST_PROVIDER.MODAL.ERROR.DESCRIPTION')}
                        </s-banner>
                    </div>
                )}
                <div className="pb-4">{t('SHIPPING_PROVIDERS.REQUEST_PROVIDER.MODAL.DESCRIPTION')}</div>
                <div className="flex flex-col gap-4">
                    <TextFieldControlledWithLabel
                        label={t('SHIPPING_PROVIDERS.REQUEST_PROVIDER.MODAL.PROVIDER_NAME.LABEL')}
                        placeholder={t('SHIPPING_PROVIDERS.REQUEST_PROVIDER.MODAL.PROVIDER_NAME.LABEL')}
                        labelHidden
                        name="provider"
                        annotated={false}
                        control={form.control}
                    />
                    <SelectControlledWithLabel
                        name="countryCode"
                        annotated={false}
                        control={form.control}
                        options={countryOptions}
                        disabled={isLoadingCountries}
                        placeholder={t('SHIPPING_PROVIDERS.REQUEST_PROVIDER.MODAL.COUNTRY.PLACEHOLDER')}
                    />
                    <SelectControlledWithLabel
                        label={t('SHIPPING_PROVIDERS.REQUEST_PROVIDER.MODAL.HAS_ACCESS.LABEL')}
                        name="hasAccess"
                        annotated={false}
                        control={form.control}
                        options={hasAccessOptions}
                        details={t('SHIPPING_PROVIDERS.REQUEST_PROVIDER.MODAL.HAS_ACCESS.NOTE')}
                    />
                    <TextFieldControlledWithLabel
                        label={t('SHIPPING_PROVIDERS.REQUEST_PROVIDER.MODAL.ADDITIONAL_NOTE.LABEL')}
                        placeholder={t('SHIPPING_PROVIDERS.REQUEST_PROVIDER.MODAL.ADDITIONAL_NOTE.PLACEHOLDER')}
                        name="description"
                        annotated={false}
                        multiline
                        rows={4}
                        control={form.control}
                    />
                </div>
            </div>
            <s-divider />
            <div className="p-4">
                <ModalActions
                    onSubmit={form.handleSubmit(handleRequestProvider)}
                    submitLabel={t('SHIPPING_PROVIDERS.REQUEST_PROVIDER.MODAL.SUBMIT')}
                    loading={isSubmitting}
                    disabledSubmit={!form.formState.isValid}
                    inlineAlign="end"
                />
            </div>
        </FormProvider>
    );
};

interface RequestProviderModalProps {
    onCancel: () => void;
    onSuccess: () => void;
    selectedCountryCode?: string;
    /** Prefills the provider name, e.g. when requested from a coming soon provider's card */
    providerName?: string;
}
